import {
  ArrowBack as BackIcon,
  CheckCircle as CheckIcon,
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
  Psychology as InvestigationIcon,
  Link as LinkIcon,
  Refresh as RefreshIcon,
  Speed as SpeedIcon,
  Subject as SubjectIcon,
  AccessTime as TimeIcon,
  SmartDisplay as VideoTabIcon,
} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Link,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import CitationHelpDialog from '../components/CitationHelpDialog';
import TranscriptTab from '../components/TranscriptTab';
import VideoAnalysisTab from '../components/VideoAnalysisTab';
import { useAuth } from '../contexts/AuthContext';
import { useThemeMode } from '../contexts/ThemeContext';
import { useThemeColors } from '../hooks/useThemeColors';
import { getVerificationStatus, verifyFactsAPI } from '../services/api';
import {
  getFactCheckItem,
  getItemVerificationResults,
  updateFactCheckItem,
  updateItemVerificationResult,
} from '../services/factCheckItemService';
import { addInvestigationItem } from '../services/firestore';
import { fetchRefinedTranscript, getValidPdfUrl, getValidVideoUrl } from '../services/gcs';
import { IMG } from '../theme/assets';
import { type VerdictKey, verdictKeyFromLabel, verdictSealFromLabel } from '../theme/designTokens';
import type {
  FactCheckItem,
  IndexedVerificationResult,
  RefinedSegment,
  VerificationSourceData,
} from '../types';
import { getQuote } from '../utils/quote';
import { calculateVerificationStats } from '../utils/statsUtils';

const ItemDetail: React.FC = () => {
  // /items/:itemId または /results/:sessionId のどちらでもアクセス可能
  const params = useParams<{ itemId?: string; sessionId?: string }>();
  const rawId = params.itemId || params.sessionId;
  // URLパラメータをデコード（日本語IDに対応）
  const itemId = rawId ? decodeURIComponent(rawId) : undefined;

  console.log('📄 ItemDetail itemId:', { rawId, decodedItemId: itemId });
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { themeMode } = useThemeMode();
  const tokens = useThemeColors();

  // テーマカラー変数
  const panelBg = themeMode === 'dark' ? 'rgba(30, 41, 59, 0.6)' : 'rgba(255, 255, 255, 0.85)';
  const borderColor = themeMode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)';
  const cardBorder = themeMode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.08)';
  const textPrimary = themeMode === 'dark' ? '#fff' : '#1a1a2e';
  const textSecondary = themeMode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)';
  const textMuted = themeMode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.45)';
  const cardBg = themeMode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)';
  const evidenceBg = themeMode === 'dark' ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.05)';
  const evidenceText = themeMode === 'dark' ? '#cbd5e1' : '#475569';
  const labelText = themeMode === 'dark' ? '#e2e8f0' : '#374151';
  const claimText = themeMode === 'dark' ? '#f1f5f9' : '#1e293b';
  const accentColor = themeMode === 'dark' ? '#64ffda' : '#00897b';
  const accordionBg = themeMode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.03)';

  // factクエリパラメータ（チャットからのファクト参照リンク）
  const targetFactIndex = searchParams.get('fact');
  const factCardRefs = useRef<{ [key: number]: HTMLElement | null }>({});

  // 案件からの遷移の場合、戻り先を案件に設定
  const fromWorkspace = (location.state as any)?.fromWorkspace;
  const backPath = fromWorkspace ? `/share/${fromWorkspace}` : '/';
  const backLabel = fromWorkspace ? '案件に戻る' : 'マイページに戻る';
  const [item, setItem] = useState<FactCheckItem | null>(null);
  const [results, setResults] = useState<IndexedVerificationResult[]>([]);
  const [filteredResults, setFilteredResults] = useState<IndexedVerificationResult[]>([]);
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [reVerifyingStates, setReVerifyingStates] = useState<boolean[]>([]);
  const [reVerifiedResults, setReVerifiedResults] = useState<Set<number>>(new Set());
  const [addedToInvestigation, setAddedToInvestigation] = useState<Set<number>>(new Set());

  // 動画関連の状態
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoUrl, setVideoUrl] = useState<string>('');
  const [playbackRate, setPlaybackRate] = useState<number>(1.0);
  const [activeTab, setActiveTab] = useState<'analysis' | 'transcript'>('analysis');
  const [refinedTranscript, setRefinedTranscript] = useState<RefinedSegment[]>([]);
  const [isTranscriptLoading, setIsTranscriptLoading] = useState(false);
  const [speedAnchor, setSpeedAnchor] = useState<null | HTMLElement>(null);
  const tabInitRef = useRef(false);

  // 動画タイプかどうかを判定
  const isVideoItem = item?.type === 'video_upload' || item?.type === 'video_url';

  // 初期サブタブ: 映像解析が未完了なら文字起こしを既定で開く（初回のみ・手動切替は尊重）
  useEffect(() => {
    if (item && isVideoItem && !tabInitRef.current) {
      tabInitRef.current = true;
      setActiveTab(item.analysisStatus === 'completed' ? 'analysis' : 'transcript');
    }
  }, [item, isVideoItem]);

  // タイムコードフォーマット関数
  const formatTimecode = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // 動画シーク関数
  const handleSeekToTime = (seconds: number) => {
    if (videoRef.current) {
      videoRef.current.currentTime = seconds;
      videoRef.current.play();
    }
  };

  useEffect(() => {
    const loadItem = async () => {
      if (itemId) {
        try {
          const foundItem = await getFactCheckItem(itemId);
          if (foundItem) {
            // PDF URLの期限切れチェック & 自動更新
            try {
              const validPdfUrl = await getValidPdfUrl(itemId, foundItem.fileUrl || '');

              // URLが更新された場合、Firestoreにも保存
              if (validPdfUrl !== foundItem.fileUrl) {
                console.log('🔄 PDF URLが更新されたため、Firestoreに保存します');
                try {
                  await updateFactCheckItem(itemId, { fileUrl: validPdfUrl });
                  foundItem.fileUrl = validPdfUrl;
                  console.log('✅ Firestoreに新しいPDF URLを保存しました');
                } catch (firestoreError) {
                  console.error('❌ Firestore更新エラー:', firestoreError);
                  foundItem.fileUrl = validPdfUrl;
                }
              }
            } catch (error) {
              console.error('PDF URL更新エラー:', error);
            }

            setItem(foundItem);

            // サブコレクションから検証結果を取得
            const resultsData = await getItemVerificationResults(itemId);
            const indexedResults = resultsData.map((result: any, index: number) => ({
              ...result,
              originalIndex: result.originalIndex ?? index,
              factId: result.factId ?? `${itemId}-fact-${index}`,
            }));
            setResults(indexedResults);
          }
        } catch (error) {
          console.error('アイテム読み込みエラー:', error);
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };

    loadItem();
  }, [itemId]);

  // 動画URL・文字起こしの読み込み
  useEffect(() => {
    const loadVideoData = async () => {
      if (!item || !itemId) return;

      const isVideo = item.type === 'video_upload' || item.type === 'video_url';
      if (!isVideo) return;

      // 動画URLの取得
      try {
        const currentVideoUrl = item.type === 'video_upload' ? item.fileUrl : item.sourceUrl;
        if (currentVideoUrl && item.blobName) {
          const validUrl = await getValidVideoUrl(itemId, currentVideoUrl, item.blobName);
          setVideoUrl(validUrl);
        }
      } catch (error) {
        console.error('動画URL取得エラー:', error);
      }

      // 文字起こしの取得
      try {
        setIsTranscriptLoading(true);
        const transcript = await fetchRefinedTranscript(itemId);
        if (transcript) {
          setRefinedTranscript(transcript.segments);
        }
      } catch (error) {
        console.error('文字起こし取得エラー:', error);
      } finally {
        setIsTranscriptLoading(false);
      }
    };

    loadVideoData();
  }, [item, itemId]);

  // 再検証状態の初期化
  useEffect(() => {
    setReVerifyingStates(new Array(results.length).fill(false));
  }, [results.length]);

  // フィルタリング機能
  useEffect(() => {
    if (activeFilter === null) {
      setFilteredResults(results);
    } else {
      // 絞り込みのキーと判定の状態を対応づける（文字そのものは見ない）
      const FILTER_TO_VERDICT: Record<string, VerdictKey> = {
        accurate: 'accurate',
        partially: 'partial',
        inaccurate: 'inaccurate',
        unverifiable: 'unverifiable',
        error: 'error',
      };
      const want = FILTER_TO_VERDICT[activeFilter];
      const filtered = results.filter((result) =>
        want ? verdictKeyFromLabel(result.result) === want : true,
      );
      setFilteredResults(filtered);
    }
  }, [results, activeFilter]);

  // ファクト参照リンクからのスクロール処理
  useEffect(() => {
    if (targetFactIndex !== null && !loading && filteredResults.length > 0) {
      const factIndex = parseInt(targetFactIndex, 10);
      if (!Number.isNaN(factIndex) && factCardRefs.current[factIndex]) {
        // 少し遅延させてDOMの描画を待つ
        setTimeout(() => {
          factCardRefs.current[factIndex]?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
          // ハイライトアニメーション用のクラスを追加
          factCardRefs.current[factIndex]?.classList.add('fact-highlight');
          setTimeout(() => {
            factCardRefs.current[factIndex]?.classList.remove('fact-highlight');
          }, 2000);
        }, 300);
      }
    }
  }, [targetFactIndex, loading, filteredResults]);

  const handleFilterClick = (filterType: string) => {
    setActiveFilter(activeFilter === filterType ? null : filterType);
  };

  const parseResultSource = (source: string): VerificationSourceData | null => {
    try {
      return JSON.parse(source);
    } catch (e) {
      console.error('結果データの解析エラー:', e);
      return null;
    }
  };

  // 出典（参照したページ）が付いていないか。
  // Web検索を選んでいても、モデルが検索不要と判断すると出典は付かない。
  const hasNoSources = (source: string): boolean => {
    const resultData = parseResultSource(source);
    return !resultData?.sources || resultData.sources.length === 0;
  };

  // AIの知識のみで検証したか（Web検索を使っていないか）
  const isInternalKnowledge = (method?: string): boolean =>
    (method || '').includes('internal_knowledge');

  // 深掘り調査対象に追加（統合版）
  const handleAddToInvestigation = async (index: number, result: IndexedVerificationResult) => {
    console.log('🔄 統合調査アイテム追加開始');
    if (!user?.uid || !item || !itemId) {
      console.error('❌ 必要な情報が不足:', { user: !!user?.uid, item: !!item, itemId: !!itemId });
      return;
    }

    try {
      console.log('📝 統合アイテム作成中...', { userId: user.uid, itemId: item.id, index });

      // source.summaryを抽出
      const sourceData = parseResultSource(result.source);
      const verificationSummary = sourceData?.summary || '';

      // 新しい統合システムを使用 - FactCheckItemをFactCheckProject形式に変換
      const projectData = {
        id: item.id,
        title: item.title,
        description: item.description || '',
        pdf_filename: item.fileName || '',
        pdf_url: item.fileUrl || '',
        pdf_blob_name: item.blobName || '',
        status: item.status,
        total_facts: item.total_facts,
        verified_facts: item.verified_facts,
        facts: item.facts,
        verification_results: item.verification_results,
        created_at: item.createdAt,
        updated_at: item.updatedAt,
        userId: item.createdBy,
      };

      const investigationItemId = await addInvestigationItem(user.uid, projectData as any, index, {
        claim: result.claim,
        result: result.result,
        confidence: result.confidence,
        verification_summary: verificationSummary,
      });

      console.log('✅ 統合アイテム作成成功:', investigationItemId);
      setAddedToInvestigation((prev) => new Set([...prev, index]));
    } catch (error) {
      console.error('❌ 統合調査アイテム追加エラー:', error);
      alert(`追加に失敗しました: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  // Google検索での再検証
  const handleReVerify = async (index: number, result: IndexedVerificationResult) => {
    if (!itemId || !item) return;

    // 再検証状態を開始
    setReVerifyingStates((prev) => prev.map((state, i) => (i === index ? true : state)));

    try {
      // アイテムの対応するファクトデータを取得
      const originalFact = item.facts[index];
      if (!originalFact) {
        console.error('対応するファクトが見つかりません:', index);
        return;
      }

      // 再検証用の一意なセッションIDを生成
      const reVerifySessionId = `${itemId}-reverify-${index}-${Date.now()}`;

      // 元のファクトデータをGoogle検索戦略で再構築
      const factForReVerification = {
        claim: originalFact.claim,
        quote: getQuote(originalFact) || getQuote(result),
        verification_strategy: 'google_search' as const,
        page: originalFact.page || 1,
        line_start: originalFact.line_start || 1,
        confidence: originalFact.confidence || 0.8,
      };

      console.log('🔄 再検証開始:', { sessionId: reVerifySessionId, fact: factForReVerification });

      // Google検索で再検証実行
      const response = await verifyFactsAPI(reVerifySessionId, [factForReVerification]);

      if (!response.success) {
        console.error('再検証開始エラー:', response.error);
        return;
      }

      console.log('✅ 再検証リクエスト送信完了、ポーリング開始');

      // ポーリングで結果を待つ（最大60秒、1秒間隔）
      const maxAttempts = 60;
      let attempts = 0;
      let newResult = null;

      while (attempts < maxAttempts) {
        await new Promise((resolve) => setTimeout(resolve, 1000)); // 1秒待機
        attempts++;

        const statusResponse = await getVerificationStatus(reVerifySessionId);

        if (!statusResponse.success) {
          console.warn(`ステータス取得失敗 (試行 ${attempts}):`, statusResponse.error);
          continue;
        }

        const status = statusResponse.data;
        console.log(
          `📊 ポーリング ${attempts}回目: status=${status?.status}, completed=${status?.completed_count}/${status?.total_count}`,
        );

        if (status?.status === 'completed' && status.results.length > 0) {
          newResult = status.results[0];
          console.log('✅ 再検証完了:', newResult);
          break;
        }

        if (status?.status === 'error') {
          console.error('再検証処理エラー');
          break;
        }
      }

      if (newResult) {
        // 元レコードの originalIndex / factId を引き継いだ結果を構築
        const original = results[index];
        const correctedResult = {
          ...newResult,
          originalIndex: original?.originalIndex ?? index,
          factId: original?.factId ?? `${itemId}-fact-${index}`,
        };

        // 画面の即時反映（該当indexのみ差し替え）
        setResults((prev) => prev.map((r, i) => (i === index ? correctedResult : r)));

        // 再検証完了をマーク
        setReVerifiedResults((prev) => new Set([...prev, index]));

        // 永続化: 表示の読み出し元と同じサブコレクション(verification_results/{index})の
        // 該当レコードだけを backend 経由で上書きする。これによりリロード後も結果が保持される。
        if (user?.uid) {
          await updateItemVerificationResult(
            itemId,
            correctedResult.originalIndex,
            correctedResult,
          );
          setItem((prev) => (prev ? { ...prev, updatedAt: new Date().toISOString() } : prev));
        }

        console.log('💾 再検証結果をサブコレクションに保存完了');
      } else {
        console.error('再検証結果を取得できませんでした（タイムアウトまたはエラー）');
      }
    } catch (error) {
      console.error('再検証エラー:', error);
      // エラーハンドリング（必要に応じてアラート表示）
    } finally {
      // 再検証状態を終了
      setReVerifyingStates((prev) => prev.map((state, i) => (i === index ? false : state)));
    }
  };

  const calculateStats = () => {
    console.log(
      'Results for stats calculation:',
      results.map((r) => ({ result: r.result, claim: r.claim })),
    );

    // 内訳と合計がずれないよう共通の集計に寄せる（#144）
    return calculateVerificationStats(results);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!item) {
    return (
      <Box>
        <Button startIcon={<BackIcon />} onClick={() => navigate(backPath)} sx={{ mb: 3 }}>
          {backLabel}
        </Button>
        <Alert severity="error">アイテムが見つかりませんでした。</Alert>
      </Box>
    );
  }

  const stats = calculateStats();

  return (
    <Box sx={{ width: '100%', height: 'calc(var(--app-vh) - 64px - 64px)', overflow: 'hidden' }}>
      {/* ヘッダー（mock .rhead: 帯なし・ページ背景に直置き） */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          px: 1.5,
          py: 1.5,
          zIndex: 1,
        }}
      >
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate(backPath)}
          sx={{
            background: themeMode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.9)',
            border: `1px solid ${borderColor}`,
            borderRadius: '11px',
            px: 1.75,
            py: 0.9,
            fontSize: 13,
            fontWeight: 700,
            color: textPrimary,
            textTransform: 'none',
            '&:hover': {
              background: themeMode === 'dark' ? 'rgba(255,255,255,0.1)' : '#fff',
            },
          }}
        >
          {backLabel}
        </Button>

        <Typography
          sx={{ fontSize: 21, fontWeight: 900, letterSpacing: '-0.01em', color: textPrimary }}
        >
          ファクトチェック結果:{' '}
          <Box component="span" sx={{ color: tokens.teal }}>
            {item.title}
          </Box>
        </Typography>

        <Button
          startIcon={<InfoIcon />}
          onClick={() => setShowLocationModal(true)}
          sx={{
            background: themeMode === 'dark' ? 'rgba(129,140,248,0.12)' : '#eef0ff',
            border: `1px solid ${themeMode === 'dark' ? 'rgba(129,140,248,0.3)' : '#d9ddff'}`,
            borderRadius: '11px',
            px: 1.75,
            py: 0.9,
            fontSize: 12.5,
            fontWeight: 800,
            color: tokens.indigo,
            textTransform: 'none',
            '&:hover': {
              background: themeMode === 'dark' ? 'rgba(129,140,248,0.18)' : '#e4e7ff',
            },
          }}
        >
          引用の確認方法
        </Button>
      </Box>

      {/* 2カラムレイアウト */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 2,
          height: 'calc(100% - 70px)',
          minHeight: 0,
          overflow: 'hidden',
          p: 1.5,
        }}
      >
        {/* 左パネル - 検証結果（mock: 外枠なし） */}
        <Box
          sx={{
            height: '100%',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ px: 2, pt: 1, flexShrink: 0 }}>
            {/* サマリーヒーロー（owl + 判定ドーナツ + シール型フィルタ） */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                background:
                  themeMode === 'dark'
                    ? 'linear-gradient(120deg, rgba(102,126,234,0.16), rgba(13,148,136,0.14))'
                    : 'linear-gradient(120deg, #eef5ff, #eafaf6)',
                border: `1px solid ${borderColor}`,
                borderRadius: '12px',
                p: 1.75,
                mb: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  component="img"
                  src={IMG.owl.clipboard}
                  alt=""
                  sx={{
                    width: 56,
                    height: 56,
                    objectFit: 'contain',
                    flexShrink: 0,
                    filter: 'drop-shadow(0 6px 10px rgba(30,30,70,0.14))',
                  }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{ fontSize: 16, fontWeight: 900, color: textPrimary, lineHeight: 1.25 }}
                  >
                    {stats.total}件の主張を見極めました。
                  </Typography>
                  <Typography sx={{ fontSize: 11.5, color: textMuted, lineHeight: 1.45, mt: 0.25 }}>
                    抽出した主張をAIが検証し、正確・不正確・部分的に正確・検証不可に分類しました。シールで絞り込めます。
                  </Typography>
                </Box>
                <Box sx={{ position: 'relative', width: 70, height: 70, flexShrink: 0 }}>
                  {(() => {
                    const segs: [number, string][] = [
                      [stats.accurate, tokens.verdict.accurate.color],
                      [stats.partiallyAccurate, tokens.verdict.partial.color],
                      [stats.inaccurate, tokens.verdict.inaccurate.color],
                      [stats.unverifiable, tokens.verdict.unverifiable.color],
                    ];
                    const total = stats.total || 1;
                    const r = 44;
                    const C = 2 * Math.PI * r;
                    let off = 0;
                    return (
                      <svg viewBox="0 0 104 104" style={{ width: 70, height: 70 }}>
                        <circle
                          cx={52}
                          cy={52}
                          r={r}
                          fill="none"
                          stroke={themeMode === 'dark' ? 'rgba(255,255,255,0.1)' : '#eef0f4'}
                          strokeWidth={13}
                        />
                        {segs.map(([n, c], i) => {
                          const len = (C * n) / total;
                          const node = (
                            <circle
                              key={i}
                              cx={52}
                              cy={52}
                              r={r}
                              fill="none"
                              stroke={c}
                              strokeWidth={13}
                              strokeDasharray={`${len} ${C - len}`}
                              strokeDashoffset={-off}
                              transform="rotate(-90 52 52)"
                            />
                          );
                          off += len;
                          return node;
                        })}
                      </svg>
                    );
                  })()}
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography
                      sx={{ fontSize: 17, fontWeight: 900, lineHeight: 1, color: textPrimary }}
                    >
                      {stats.total}
                    </Typography>
                    <Typography sx={{ fontSize: 9, color: textMuted }}>件</Typography>
                  </Box>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {[
                  { key: 'accurate', label: '正確', count: stats.accurate, vkey: 'accurate' },
                  {
                    key: 'partially',
                    label: '部分的',
                    count: stats.partiallyAccurate,
                    vkey: 'partial',
                  },
                  {
                    key: 'inaccurate',
                    label: '不正確',
                    count: stats.inaccurate,
                    vkey: 'inaccurate',
                  },
                  {
                    key: 'unverifiable',
                    label: '検証不可',
                    count: stats.unverifiable,
                    vkey: 'unverifiable',
                  },
                  // 失敗したものがあるときだけ出す。黙って消すと内訳と合計が合わない
                  ...(stats.error > 0
                    ? [{ key: 'error', label: 'エラー', count: stats.error, vkey: 'error' }]
                    : []),
                ].map((f) => {
                  const v = tokens.verdict[f.vkey as keyof typeof tokens.verdict];
                  const on = activeFilter === f.key;
                  return (
                    <Box
                      key={f.key}
                      component="button"
                      onClick={() => handleFilterClick(f.key)}
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        fontFamily: 'inherit',
                        border: `1.5px solid ${on ? v.color : borderColor}`,
                        borderRadius: '11px',
                        px: 1,
                        py: 0.75,
                        cursor: 'pointer',
                        background: on
                          ? v.tint
                          : themeMode === 'dark'
                            ? 'rgba(255,255,255,0.04)'
                            : '#fff',
                        transition: '0.15s',
                        '&:hover': { transform: 'translateY(-1px)' },
                      }}
                    >
                      <Box
                        component="img"
                        src={IMG.verdict[f.vkey as keyof typeof IMG.verdict]}
                        alt=""
                        sx={{ width: 26, height: 26, objectFit: 'contain', flexShrink: 0 }}
                      />
                      <Box sx={{ minWidth: 0, textAlign: 'left' }}>
                        <Typography
                          sx={{ fontSize: 15, fontWeight: 900, lineHeight: 1, color: v.color }}
                        >
                          {f.count}
                        </Typography>
                        <Typography
                          sx={{ fontSize: 9, color: textMuted, mt: '1px', whiteSpace: 'nowrap' }}
                        >
                          {f.label}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>

            {/* フィルター時のメッセージ（結果タブの時のみ表示） */}
            {activeFilter !== null && (
              <Box sx={{ mb: 2, mt: isVideoItem ? 2 : 0 }}>
                <Alert severity="info">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                      再調査機能は全件表示時のみ利用できます。
                    </Typography>
                    <Button
                      onClick={() => setActiveFilter(null)}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.75rem', py: 0.25, px: 1 }}
                    >
                      全件表示に切り替え
                    </Button>
                  </Box>
                </Alert>
              </Box>
            )}
          </Box>

          {/* 検証済みファクト一覧（結果タブ） */}
          <Box
            sx={{
              flexGrow: 1,
              overflowY: 'auto',
              minHeight: 0,
              p: 2,
              display: 'block',
            }}
          >
            {filteredResults.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" sx={{ color: textSecondary }}>
                  該当する検証結果がありません
                </Typography>
                <Button
                  onClick={() => setActiveFilter(null)}
                  sx={{ mt: 2 }}
                  variant="outlined"
                  size="small"
                >
                  フィルターをクリア
                </Button>
              </Box>
            ) : (
              // 元の順序でソートして表示
              filteredResults
                .sort((a, b) => (a.originalIndex ?? 0) - (b.originalIndex ?? 0))
                .map((result, displayIndex) => {
                  const resultData = parseResultSource(result.source);
                  const originalIndex = result.originalIndex ?? displayIndex;

                  return (
                    <Card
                      key={`result-${originalIndex}`}
                      ref={(el) => {
                        factCardRefs.current[originalIndex] = el;
                      }}
                      sx={{
                        mb: 2,
                        background: themeMode === 'dark' ? cardBg : tokens.paper,
                        border: `1px solid ${cardBorder}`,
                        borderLeft: '5px solid',
                        borderLeftColor: tokens.verdict[verdictKeyFromLabel(result.result)].color,
                        borderRadius: '13px',
                        overflow: 'hidden',
                        boxShadow:
                          themeMode === 'dark'
                            ? '0 4px 6px rgba(0, 0, 0, 0.1)'
                            : '0 6px 20px rgba(30, 30, 70, 0.06)',
                        transition: 'all 0.3s ease',
                        // 結果カードのホバー：色は付けず、軽い持ち上がり＋影のみ
                        '&:hover': {
                          background: themeMode === 'dark' ? cardBg : tokens.paper,
                          transform: 'translateY(-2px)',
                          borderColor: cardBorder,
                          borderLeftColor: tokens.verdict[verdictKeyFromLabel(result.result)].color,
                          boxShadow:
                            themeMode === 'dark'
                              ? '0 8px 20px rgba(0, 0, 0, 0.28)'
                              : '0 10px 24px rgba(30, 30, 70, 0.12)',
                        },
                        // ハイライトアニメーション
                        '&.fact-highlight': {
                          boxShadow: `0 0 20px ${themeMode === 'dark' ? 'rgba(100, 255, 218, 0.5)' : 'rgba(0, 137, 123, 0.3)'}`,
                          borderColor: accentColor,
                        },
                      }}
                    >
                      <CardContent sx={{ p: 2 }}>
                        {/* 判定結果ヘッダー */}
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.25,
                            mb: 2,
                            flexWrap: 'wrap',
                          }}
                        >
                          <Box
                            component="img"
                            src={verdictSealFromLabel(result.result)}
                            alt=""
                            sx={{
                              width: 40,
                              height: 40,
                              objectFit: 'contain',
                              flexShrink: 0,
                              filter: 'drop-shadow(0 3px 5px rgba(30,30,70,0.14))',
                            }}
                          />
                          {(() => {
                            const v = tokens.verdict[verdictKeyFromLabel(result.result)];
                            return (
                              <Box
                                component="span"
                                sx={{
                                  fontSize: 13,
                                  fontWeight: 900,
                                  px: 1.25,
                                  py: '3px',
                                  borderRadius: 999,
                                  bgcolor: v.tint,
                                  color: v.color,
                                }}
                              >
                                {result.result}
                              </Box>
                            );
                          })()}
                          {/* 動画: タイムコード（ドキュメントはページ表示なし） */}
                          {isVideoItem && item.facts[originalIndex]?.start_sec !== undefined ? (
                            <Box
                              component="button"
                              onClick={() => handleSeekToTime(item.facts[originalIndex].start_sec!)}
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.5,
                                fontFamily: 'inherit',
                                fontSize: 12,
                                fontWeight: 700,
                                color: '#2563eb',
                                bgcolor: themeMode === 'dark' ? 'rgba(37,99,235,0.18)' : '#e8f1ff',
                                border: 'none',
                                borderRadius: 999,
                                px: 1.25,
                                py: '3px',
                                cursor: 'pointer',
                              }}
                            >
                              <TimeIcon sx={{ fontSize: 14 }} />
                              {formatTimecode(item.facts[originalIndex].start_sec!)}
                            </Box>
                          ) : null}

                          {/* ボタングループ */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
                            {/* 内部知識検証かつフィルターなしの場合のみ再検証ボタンを表示 */}
                            {hasNoSources(result.source) &&
                              !reVerifiedResults.has(originalIndex) &&
                              activeFilter === null && (
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={
                                    reVerifyingStates[originalIndex] ? (
                                      <CircularProgress size={14} />
                                    ) : (
                                      <RefreshIcon />
                                    )
                                  }
                                  onClick={() => handleReVerify(originalIndex, result)}
                                  disabled={reVerifyingStates[originalIndex]}
                                  sx={{
                                    fontSize: 11.5,
                                    fontWeight: 800,
                                    py: 0.5,
                                    px: 1.25,
                                    textTransform: 'none',
                                    color: '#e0890c',
                                    borderColor:
                                      themeMode === 'dark' ? 'rgba(224,137,12,0.5)' : '#f3c98a',
                                    background: themeMode === 'dark' ? 'transparent' : '#fff8ec',
                                    '&:hover': {
                                      borderColor: '#e0890c',
                                      background:
                                        themeMode === 'dark' ? 'rgba(224,137,12,0.1)' : '#fdeccb',
                                    },
                                  }}
                                >
                                  {reVerifyingStates[originalIndex]
                                    ? '検証中...'
                                    : 'ソース付きで再検証'}
                                </Button>
                              )}

                            {/* 再検証完了の場合（フィルターなしの場合のみ表示） */}
                            {reVerifiedResults.has(originalIndex) && activeFilter === null && (
                              <Box
                                component="span"
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  fontSize: 11,
                                  fontWeight: 800,
                                  borderRadius: 999,
                                  px: 1.25,
                                  py: '5px',
                                  bgcolor: tokens.verdict.accurate.tint,
                                  color: tokens.verdict.accurate.color,
                                }}
                              >
                                <CheckIcon sx={{ fontSize: 14 }} /> 再調査完了
                              </Box>
                            )}

                            {/* AIによる追加リサーチボタン */}
                            {!addedToInvestigation.has(originalIndex) ? (
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<InvestigationIcon />}
                                onClick={() => handleAddToInvestigation(originalIndex, result)}
                                sx={{
                                  fontSize: 11.5,
                                  fontWeight: 800,
                                  py: 0.5,
                                  px: 1.25,
                                  textTransform: 'none',
                                  color: tokens.indigo,
                                  borderColor:
                                    themeMode === 'dark' ? 'rgba(129,140,248,0.5)' : '#cfd0ff',
                                  background: themeMode === 'dark' ? 'transparent' : '#f0f0ff',
                                  '&:hover': {
                                    borderColor: tokens.indigo,
                                    background:
                                      themeMode === 'dark' ? 'rgba(129,140,248,0.1)' : '#e6e6ff',
                                  },
                                }}
                              >
                                AIによる追加リサーチ
                              </Button>
                            ) : (
                              <Box
                                component="span"
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  fontSize: 11,
                                  fontWeight: 800,
                                  borderRadius: 999,
                                  px: 1.25,
                                  py: '5px',
                                  bgcolor:
                                    themeMode === 'dark' ? 'rgba(129,140,248,0.18)' : '#ede9ff',
                                  color: tokens.indigo,
                                }}
                              >
                                <CheckIcon sx={{ fontSize: 14 }} /> 追加済み
                              </Box>
                            )}
                          </Box>
                        </Box>

                        {/* 主張 */}
                        <Box sx={{ mb: 1.5 }}>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: labelText, mb: 0.5 }}
                          >
                            主張 (Claim)
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ lineHeight: 1.6, fontSize: '0.875rem', color: claimText }}
                          >
                            {result.claim}
                          </Typography>
                        </Box>

                        {/* 引用 */}
                        <Box sx={{ mb: 1.5 }}>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: labelText, mb: 0.5 }}
                          >
                            引用
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              backgroundColor: evidenceBg,
                              p: 1,
                              borderRadius: 1,
                              fontSize: '0.875rem',
                              lineHeight: 1.4,
                              color: evidenceText,
                              border: `1px solid ${borderColor}`,
                            }}
                          >
                            {getQuote(result) || `${result.claim.substring(0, 50)}...`}
                          </Typography>
                        </Box>

                        {/* 要約 */}
                        {resultData && (
                          <Box sx={{ mb: 2 }}>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600, color: labelText, mb: 0.5 }}
                            >
                              要約
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontSize: '0.875rem', lineHeight: 1.6, color: evidenceText }}
                            >
                              {resultData.summary}
                            </Typography>
                          </Box>
                        )}

                        {/* 詳細情報（アコーディオン） */}
                        {resultData && (
                          <Accordion
                            sx={{
                              boxShadow: 'none',
                              border: `1px solid ${borderColor}`,
                              background: accordionBg,
                              color: labelText,
                            }}
                          >
                            <AccordionSummary
                              expandIcon={<ExpandMoreIcon sx={{ color: textSecondary }} />}
                              sx={{
                                minHeight: 'auto',
                                py: 1,
                                outline: 'none',
                                '& .MuiAccordionSummary-content': { margin: '4px 0' },
                                '&.Mui-focusVisible': { backgroundColor: 'transparent !important' },
                                '&:focus, &:focus-visible': { outline: 'none', boxShadow: 'none' },
                                '& .MuiTouchRipple-root': { display: 'none' },
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 600, fontSize: '0.875rem' }}
                              >
                                詳しく見る
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails
                              sx={{
                                pt: 1,
                                pb: 1.5,
                                px: 1.75,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1.75,
                              }}
                            >
                              {/* 詳細な理由 */}
                              <Box>
                                <Typography
                                  sx={{
                                    fontSize: 11,
                                    fontWeight: 800,
                                    color: tokens.teal,
                                    letterSpacing: '.02em',
                                    mb: 0.6,
                                  }}
                                >
                                  詳細な理由
                                </Typography>
                                <Typography
                                  sx={{ fontSize: 12.5, lineHeight: 1.6, color: evidenceText }}
                                >
                                  {resultData.reason}
                                </Typography>
                              </Box>

                              {/* 出典が無い場合は、その理由が分かるようにする */}
                              {(!resultData.sources || resultData.sources.length === 0) && (
                                <Box>
                                  <Typography
                                    sx={{
                                      fontSize: 11,
                                      fontWeight: 800,
                                      color: tokens.muted,
                                      letterSpacing: '.02em',
                                      mb: 0.5,
                                    }}
                                  >
                                    情報源
                                  </Typography>
                                  <Typography
                                    sx={{ fontSize: 12, color: tokens.muted, lineHeight: 1.5 }}
                                  >
                                    {isInternalKnowledge(result.method)
                                      ? 'AIの知識のみで検証したため、参照元はありません。'
                                      : 'AIが検索不要と判断したため、参照元はありません。'}
                                  </Typography>
                                </Box>
                              )}

                              {/* 情報源（検索で実際に参照したページ） */}
                              {resultData.sources && resultData.sources.length > 0 && (
                                <Box>
                                  <Typography
                                    sx={{
                                      fontSize: 11,
                                      fontWeight: 800,
                                      color: tokens.teal,
                                      letterSpacing: '.02em',
                                      mb: 0.75,
                                    }}
                                  >
                                    情報源
                                  </Typography>
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
                                    {resultData.sources.map((source, sourceIndex) => (
                                      <Box
                                        key={sourceIndex}
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'flex-start',
                                          gap: 0.75,
                                          fontSize: 12,
                                          lineHeight: 1.5,
                                        }}
                                      >
                                        <LinkIcon
                                          sx={{
                                            fontSize: 14,
                                            color: accentColor,
                                            mt: '2px',
                                            flexShrink: 0,
                                          }}
                                        />
                                        {source.url ? (
                                          <Link
                                            href={source.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            sx={{
                                              textDecoration: 'none',
                                              color: accentColor,
                                              wordBreak: 'break-word',
                                              '&:hover': { textDecoration: 'underline' },
                                            }}
                                          >
                                            {source.title}
                                          </Link>
                                        ) : (
                                          <Box component="span" sx={{ color: textSecondary }}>
                                            {source.title}
                                          </Box>
                                        )}
                                      </Box>
                                    ))}
                                  </Box>
                                </Box>
                              )}

                              {/* 思考プロセス（Google検索の場合のみ） */}
                              {resultData.thought_process && (
                                <Box>
                                  <Typography
                                    sx={{
                                      fontSize: 11,
                                      fontWeight: 800,
                                      color: tokens.teal,
                                      letterSpacing: '.02em',
                                      mb: 0.6,
                                    }}
                                  >
                                    思考プロセス
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontSize: 12,
                                      lineHeight: 1.6,
                                      whiteSpace: 'pre-wrap',
                                      color: textSecondary,
                                      pl: 1.25,
                                      borderLeft: `2px solid ${borderColor}`,
                                    }}
                                  >
                                    {resultData.thought_process}
                                  </Typography>
                                </Box>
                              )}
                            </AccordionDetails>
                          </Accordion>
                        )}
                      </CardContent>
                    </Card>
                  );
                })
            )}
          </Box>
        </Box>

        {/* 右パネル - プレビュー（PDF または 動画） */}
        <Box
          sx={{
            height: '100%',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            ...(isVideoItem
              ? {}
              : {
                  background: themeMode === 'dark' ? panelBg : tokens.paper,
                  border: `1px solid ${tokens.line}`,
                  borderRadius: '13px',
                  boxShadow: themeMode === 'dark' ? 'none' : '0 8px 24px rgba(30,30,70,0.06)',
                  p: 2,
                }),
          }}
        >
          {isVideoItem ? (
            <>
              {/* 動画プレビュー（カード） */}
              <Box
                sx={{
                  flexShrink: 0,
                  mb: 2,
                  background: themeMode === 'dark' ? panelBg : tokens.paper,
                  border: `1px solid ${tokens.line}`,
                  borderRadius: '18px',
                  boxShadow: themeMode === 'dark' ? 'none' : '0 8px 24px rgba(30,30,70,0.06)',
                  p: 1.5,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
                  <Box
                    component="img"
                    src={IMG.preview.video}
                    alt=""
                    sx={{ width: 26, height: 26, objectFit: 'contain' }}
                  />
                  <Typography sx={{ fontSize: 14, fontWeight: 900, color: textPrimary }}>
                    動画プレビュー
                  </Typography>
                  {videoUrl && (
                    <>
                      <Button
                        size="small"
                        startIcon={<SpeedIcon sx={{ fontSize: 16 }} />}
                        endIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
                        onClick={(e) => setSpeedAnchor(e.currentTarget)}
                        sx={{
                          ml: 'auto',
                          color: tokens.ink,
                          fontWeight: 800,
                          fontSize: 12.5,
                          border: `1px solid ${tokens.line}`,
                          borderRadius: 2,
                          px: 1.25,
                          backgroundColor: tokens.paper,
                        }}
                      >
                        {playbackRate}x
                      </Button>
                      <Menu
                        anchorEl={speedAnchor}
                        open={Boolean(speedAnchor)}
                        onClose={() => setSpeedAnchor(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                      >
                        {[0.5, 0.75, 1.0, 1.25, 1.5, 2.0, 2.5, 3.0].map((r) => (
                          <MenuItem
                            key={r}
                            selected={r === playbackRate}
                            onClick={() => {
                              setPlaybackRate(r);
                              if (videoRef.current) videoRef.current.playbackRate = r;
                              setSpeedAnchor(null);
                            }}
                            sx={{
                              fontSize: 13,
                              fontWeight: 700,
                              minWidth: 110,
                              color: r === playbackRate ? tokens.teal : tokens.muted,
                            }}
                          >
                            {r}x
                          </MenuItem>
                        ))}
                      </Menu>
                    </>
                  )}
                </Box>
                {videoUrl ? (
                  <video
                    ref={videoRef}
                    src={videoUrl}
                    controls
                    style={{
                      width: '100%',
                      maxHeight: 320,
                      objectFit: 'contain',
                      borderRadius: 8,
                      backgroundColor: '#000',
                      display: 'block',
                    }}
                    onLoadedMetadata={() => {
                      if (videoRef.current) videoRef.current.playbackRate = playbackRate;
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      aspectRatio: '16/9',
                      borderRadius: '12px',
                      background: 'linear-gradient(135deg, #1b2030, #39405a)',
                      display: 'grid',
                      placeItems: 'center',
                      color: 'rgba(255,255,255,0.55)',
                    }}
                  >
                    <Box
                      component="svg"
                      viewBox="0 0 24 24"
                      sx={{ width: 64, height: 64, fill: 'currentColor' }}
                    >
                      <path d="M8 5v14l11-7z" />
                    </Box>
                  </Box>
                )}
              </Box>

              {/* サブタブ（カード外） */}
              <Box
                sx={{
                  display: 'flex',
                  gap: 3,
                  borderBottom: `1px solid ${tokens.line}`,
                  mb: 1.5,
                  flexShrink: 0,
                }}
              >
                {(
                  [
                    {
                      key: 'analysis',
                      label: '映像解析',
                      icon: <VideoTabIcon sx={{ fontSize: 17 }} />,
                    },
                    {
                      key: 'transcript',
                      label: '文字起こし',
                      icon: <SubjectIcon sx={{ fontSize: 17 }} />,
                    },
                  ] as const
                ).map((tab) => {
                  const on = activeTab === tab.key;
                  return (
                    <Box
                      key={tab.key}
                      onClick={() => setActiveTab(tab.key)}
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.75,
                        fontSize: 13.5,
                        fontWeight: 800,
                        color: on ? tokens.teal : tokens.muted,
                        pt: '8px',
                        pb: '10px',
                        cursor: 'pointer',
                        position: 'relative',
                        '&::after': on
                          ? {
                              content: '""',
                              position: 'absolute',
                              left: 0,
                              right: 0,
                              bottom: '-1px',
                              height: '3px',
                              borderRadius: '3px 3px 0 0',
                              backgroundColor: tokens.teal,
                            }
                          : undefined,
                      }}
                    >
                      {tab.icon}
                      {tab.label}
                    </Box>
                  );
                })}
              </Box>

              {/* サブタブ内容（カード外・スクロール） */}
              <Box sx={{ flexGrow: 1, overflowY: 'auto', minHeight: 0 }}>
                {activeTab === 'analysis' && itemId ? (
                  <VideoAnalysisTab itemId={itemId} onSeek={handleSeekToTime} />
                ) : (
                  <TranscriptTab
                    segments={refinedTranscript}
                    loading={isTranscriptLoading}
                    onSeek={handleSeekToTime}
                  />
                )}
              </Box>
            </>
          ) : (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexShrink: 0 }}>
                <Box
                  component="img"
                  src={IMG.preview.document}
                  alt=""
                  sx={{ width: 28, height: 28, objectFit: 'contain' }}
                />
                <Typography sx={{ fontSize: 14, fontWeight: 900, color: textPrimary }}>
                  ドキュメントプレビュー
                </Typography>
              </Box>
              <Box sx={{ flexGrow: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                {item.fileUrl ? (
                  <iframe
                    src={item.fileUrl}
                    width="100%"
                    height="100%"
                    style={{
                      border: `1px solid ${tokens.line}`,
                      borderRadius: 10,
                      background: '#fff',
                    }}
                    title="PDF Preview"
                  />
                ) : (
                  <Box
                    sx={{
                      flexGrow: 1,
                      minHeight: 220,
                      borderRadius: '10px',
                      border: `1px solid ${tokens.line}`,
                      display: 'grid',
                      placeItems: 'center',
                      background: themeMode === 'dark' ? 'rgba(0,0,0,0.2)' : '#f6f9f8',
                    }}
                  >
                    <Typography sx={{ color: textMuted, fontSize: 13 }}>
                      PDFプレビューが利用できません
                    </Typography>
                  </Box>
                )}
              </Box>
            </>
          )}
        </Box>
      </Box>

      <CitationHelpDialog open={showLocationModal} onClose={() => setShowLocationModal(false)} />

      {/* 案件所属アイテムの場合のみフローティングチャットボタンを表示 */}
    </Box>
  );
};

export default ItemDetail;
